import type { ReactNode } from "react";

import type { AdvisorProfileModuleProps } from "./renderProps";
import { renderTextWithLineBreaks } from "./text";

const trailingCredentialMark = /([®™])(\s*)$/u;

function renderCredentialText(credential: string): ReactNode {
  const match = credential.match(trailingCredentialMark);

  if (!match || match.index === undefined) {
    return credential;
  }

  return (
    <>
      {credential.slice(0, match.index)}
      <sup>{match[1]}</sup>
      {match[2]}
    </>
  );
}

export function AdvisorProfileModule({
  name,
  title,
  credentials,
  bio,
  phone,
  email,
  calendarUrl,
  linkedInUrl,
  location,
  image,
}: AdvisorProfileModuleProps) {
  return (
    <section
      className="cms-section cms-advisor-profile"
      data-cms-module="advisor-profile"
    >
      <div className="cms-wrap">
        <div className="cms-eyebrow-row">
          <span className="cms-eyebrow">About your advisor</span>
        </div>
        <div className="cms-advisor-profile__layout">
          <div>
            {image ? (
              <figure className="cms-advisor-profile__image">
                <img src={image.src} alt={image.alt} />
                {image.caption ? (
                  <figcaption>{image.caption}</figcaption>
                ) : null}
              </figure>
            ) : null}
            {credentials?.length ? (
              <ul className="cms-advisor-profile__credentials">
                {credentials.map((credential) => (
                  <li key={credential}>{renderCredentialText(credential)}</li>
                ))}
              </ul>
            ) : null}
          </div>
          <div className="cms-advisor-profile__content">
            {name ? (
              <h2 className="cms-advisor-profile__name">{name}</h2>
            ) : null}
            {title ? (
              <p className="cms-advisor-profile__title">
                {renderTextWithLineBreaks(title)}
              </p>
            ) : null}
            {bio ? (
              <p className="cms-advisor-profile__bio">
                {renderTextWithLineBreaks(bio)}
              </p>
            ) : null}
            <div className="cms-advisor-profile__links">
              {email ? <a href={`mailto:${email}`}>{email}</a> : null}
              {phone ? <a href={`tel:${phone}`}>{phone}</a> : null}
              {calendarUrl ? (
                <a href={calendarUrl} rel="noopener noreferrer">
                  Schedule time
                </a>
              ) : null}
              {linkedInUrl ? (
                <a href={linkedInUrl} rel="noopener noreferrer">
                  LinkedIn
                </a>
              ) : null}
              {location ? <span>{location}</span> : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
